import { getCartStoreKey, getCartToken } from 'helpers/cartScope';

const DATABASE_NAME = 'jaypion-cart-outbox';
const DATABASE_VERSION = 1;
const OBJECT_STORE = 'snapshots';
const memoryFallback = {};
const latestPrepared = {};

const openDatabase = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') {
    reject(new Error('IndexedDB no está disponible'));
    return;
  }

  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(OBJECT_STORE)) {
      const store = database.createObjectStore(OBJECT_STORE, { keyPath: 'key' });
      store.createIndex('storeKey', 'storeKey', { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error('No se pudo abrir el outbox local'));
});

const mapCartItems = (items = [], products = [], unvalidated = false) => (
  (Array.isArray(items) ? items : []).map((item) => {
    const productId = Number(item.itemId);
    const product = products.find((candidate) => Number(candidate.id) === productId);
    return {
      item_id: productId,
      name: product?.name || null,
      code: product?.code || null,
      quantity: Math.max(1, Number(item.quantity) || 1),
      description_id: Number(item.descriptionId) > 0 ? Number(item.descriptionId) : null,
      talle: item.talle ? String(item.talle).slice(0, 100) : null,
      wholesale_id: Number(item.wholesale?.id) > 0 ? Number(item.wholesale.id) : null,
      unvalidated,
    };
  }).filter((item) => Number.isInteger(item.item_id) && item.item_id > 0)
);

export const createCartSnapshotItems = ({ items, itemsSub, products }) => ([
  ...mapCartItems(items, products, false),
  ...mapCartItems(itemsSub, products, true),
]);

export const prepareCartSnapshot = async ({ items, itemsSub, products, customerEmail }) => {
  const storeKey = getCartStoreKey();
  const cartToken = getCartToken();
  if (!cartToken) return null;

  const snapshotItems = createCartSnapshotItems({ items, itemsSub, products });
  const key = `${storeKey}::${cartToken}`;
  const email = String(customerEmail || '').trim().toLowerCase() || null;

  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(OBJECT_STORE, 'readwrite');
      const store = transaction.objectStore(OBJECT_STORE);
      const getRequest = store.get(key);
      let result = null;

      getRequest.onsuccess = () => {
        const existing = getRequest.result;
        const effectiveEmail = email || existing?.customerEmail || null;
        if (!existing && snapshotItems.length === 0) return;

        const fingerprint = JSON.stringify({ items: snapshotItems, customerEmail: effectiveEmail });
        if (existing?.fingerprint === fingerprint) {
          result = existing;
          latestPrepared[key] = existing;
          return;
        }

        result = {
          key,
          storeKey,
          cartToken,
          revision: (existing?.revision || 0) + 1,
          fingerprint,
          items: snapshotItems,
          customerEmail: effectiveEmail,
          status: 'pending',
          updatedAt: Date.now(),
        };
        store.put(result);
        latestPrepared[key] = result;
      };
      transaction.oncomplete = () => {
        database.close();
        if (result) resolve(result);
        else resolve(null);
      };
      transaction.onerror = () => {
        database.close();
        reject(transaction.error || new Error('No se pudo guardar el outbox local'));
      };
      transaction.onabort = () => {
        database.close();
        reject(transaction.error || new Error('Se canceló el guardado del outbox local'));
      };
    });
  } catch (error) {
    const existing = memoryFallback[key];
    if (!existing && snapshotItems.length === 0) return null;
    const effectiveEmail = email || existing?.customerEmail || null;
    const fingerprint = JSON.stringify({ items: snapshotItems, customerEmail: effectiveEmail });
    const record = existing?.fingerprint === fingerprint ? existing : {
      key,
      storeKey,
      cartToken,
      revision: (existing?.revision || 0) + 1,
      fingerprint,
      items: snapshotItems,
      customerEmail: effectiveEmail,
      status: 'pending',
      updatedAt: Date.now(),
    };
    memoryFallback[key] = record;
    latestPrepared[key] = record;
    return record;
  }
};

export const getPendingCartSnapshots = async (storeKey = getCartStoreKey()) => {
  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(OBJECT_STORE, 'readonly');
      const request = transaction.objectStore(OBJECT_STORE).index('storeKey').getAll(storeKey);
      request.onsuccess = () => resolve((request.result || []).filter((record) => record.status === 'pending'));
      request.onerror = () => reject(request.error || new Error('No se pudo leer el outbox local'));
      transaction.oncomplete = () => database.close();
      transaction.onerror = () => database.close();
    });
  } catch (error) {
    return Object.values(memoryFallback).filter((record) => record.storeKey === storeKey && record.status === 'pending');
  }
};

export const markCartSnapshotSynced = async (key, revision) => {
  try {
    const database = await openDatabase();
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(OBJECT_STORE, 'readwrite');
      const store = transaction.objectStore(OBJECT_STORE);
      const request = store.get(key);
      request.onsuccess = () => {
        const record = request.result;
        if (record && record.revision === revision) {
          record.status = 'synced';
          store.put(record);
          latestPrepared[key] = record;
        }
      };
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('No se pudo confirmar el outbox local'));
    });
    database.close();
  } catch (error) {
    if (memoryFallback[key]?.revision === revision) memoryFallback[key].status = 'synced';
  }
};

export const removeCartSnapshot = async (storeKey, cartToken) => {
  const key = `${storeKey}::${cartToken}`;
  delete memoryFallback[key];
  delete latestPrepared[key];
  try {
    const database = await openDatabase();
    await new Promise((resolve, reject) => {
      const transaction = database.transaction(OBJECT_STORE, 'readwrite');
      transaction.objectStore(OBJECT_STORE).delete(key);
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('No se pudo limpiar el outbox local'));
    });
    database.close();
  } catch (error) {
    // El carrito local sigue disponible y el servidor conserva el estado recuperado.
  }
};

export const getLatestPreparedCartSnapshot = (storeKey = getCartStoreKey()) => (
  Object.values(latestPrepared).filter((record) => record.storeKey === storeKey && record.status === 'pending')
);
